/**
 * AiAgentsPage — one admin surface for every multi-agent AI feature.
 * Lists Investigation, Sentinel, and Insight-Audit agents grouped by
 * category. Each row lets a tenant admin swap provider + model + change
 * temperature, plus reset back to the recommended preset. A compact
 * "spent $X in the last 7 days" total sits in the header.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Search, RotateCcw, DollarSign, Zap, ShieldCheck, Bell, Loader2, PlayCircle, Download, Upload, Pencil, Check, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { api } from '@/lib/api';

const CATEGORY_META = {
  investigation: { label: 'Investigation Why', icon: Sparkles, tint: 'text-indigo-400', border: 'border-indigo-500/25' },
  sentinel: { label: 'Sentinels', icon: Bell, tint: 'text-amber-400', border: 'border-amber-500/25' },
  insight: { label: 'Insights', icon: ShieldCheck, tint: 'text-emerald-400', border: 'border-emerald-500/25' },
};

function formatCost(v) {
  if (!v) return '$0';
  if (v < 0.01) return '<$0.01';
  return `$${v.toFixed(v < 1 ? 3 : 2)}`;
}

function AgentRow({ agent, providerModels, onChange, onReset, onTest, saving, testResult }) {
  const [provider, setProvider] = useState(agent.current.provider);
  const [model, setModel] = useState(agent.current.model);
  const [temperature, setTemperature] = useState(
    agent.current.temperature !== undefined && agent.current.temperature !== null
      ? String(agent.current.temperature)
      : ''
  );
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setProvider(agent.current.provider);
    setModel(agent.current.model);
    setTemperature(
      agent.current.temperature !== undefined && agent.current.temperature !== null
        ? String(agent.current.temperature)
        : ''
    );
    setDirty(false);
  }, [agent.current.provider, agent.current.model, agent.current.temperature]);

  const availableModels = useMemo(() => {
    const list = providerModels?.[provider] || [];
    // Always include the currently-selected model so we can display legacy
    // choices even when they've fallen off the curated menu.
    if (model && !list.includes(model)) return [...list, model];
    return list;
  }, [providerModels, provider, model]);

  const handleSave = async () => {
    const payload = { provider, model };
    if (temperature !== '') {
      const t = Number(temperature);
      if (!Number.isFinite(t)) { toast.error('Temperature must be a number'); return; }
      payload.temperature = t;
    }
    await onChange(agent.key, payload);
    setDirty(false);
  };

  return (
    <div className="rounded-xl border border-border/50 bg-card/50 p-4" data-testid={`agent-row-${agent.key}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-foreground">{agent.label}</h3>
            {agent.current.customised ? (
              <Badge className="text-[9px] bg-primary/15 text-primary border-primary/25">Customised</Badge>
            ) : (
              <Badge className="text-[9px] bg-muted text-muted-foreground">Recommended</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed max-w-[46rem]">{agent.description}</p>
          <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1" data-testid={`agent-cost-${agent.key}`}>
              <DollarSign className="w-3 h-3" />
              {formatCost(agent.usage_7d.cost_usd)} · {agent.usage_7d.calls} call{agent.usage_7d.calls === 1 ? '' : 's'} · 7d
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <Select
            value={provider}
            onValueChange={(v) => { setProvider(v); setDirty(true); }}
          >
            <SelectTrigger className="h-8 w-32 text-xs rounded-lg" data-testid={`agent-provider-${agent.key}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.keys(providerModels || {}).map((p) => (
                <SelectItem key={p} value={p} className="text-xs capitalize">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={model}
            onValueChange={(v) => { setModel(v); setDirty(true); }}
          >
            <SelectTrigger className="h-8 w-56 text-xs rounded-lg" data-testid={`agent-model-${agent.key}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableModels.length === 0 ? (
                <SelectItem value={model || '__none__'} className="text-xs">{model || 'No models'}</SelectItem>
              ) : availableModels.map((m) => (
                <SelectItem key={m} value={m} className="text-xs font-mono">{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={temperature}
            onChange={(e) => { setTemperature(e.target.value); setDirty(true); }}
            placeholder="temp"
            className="h-8 w-16 text-xs rounded-lg"
            data-testid={`agent-temperature-${agent.key}`}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => onReset(agent.key)}
            disabled={saving === agent.key || !agent.current.customised}
            className="h-8 gap-1 rounded-lg"
            data-testid={`agent-reset-${agent.key}`}
            title="Reset to recommended"
          >
            <RotateCcw className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onTest(agent.key)}
            disabled={saving === agent.key}
            className="h-8 gap-1 rounded-lg text-xs"
            data-testid={`agent-test-${agent.key}`}
            title="Test connection with a tiny health-check prompt"
          >
            {testResult?.loading ? <Loader2 className="w-3 h-3 animate-spin" /> :
              testResult?.ok === true ? <Check className="w-3 h-3 text-emerald-400" /> :
              <PlayCircle className="w-3 h-3" />}
            Test
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving === agent.key || !dirty}
            className="h-8 rounded-lg text-xs"
            data-testid={`agent-save-${agent.key}`}
          >
            {saving === agent.key ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>
      {testResult && !testResult.loading && (
        <div
          className={`mt-2 rounded-md px-2 py-1.5 text-[10px] ${testResult.ok ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/25' : 'bg-destructive/10 text-destructive border border-destructive/25'}`}
          data-testid={`agent-test-result-${agent.key}`}
        >
          {testResult.ok
            ? `OK · ${testResult.latency_ms} ms · ${testResult.model} · sample: ${testResult.sample?.slice(0, 60) || '(empty)'}`
            : `Failed: ${testResult.error}`}
        </div>
      )}
    </div>
  );
}

export default function AiAgentsPage() {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [query, setQuery] = useState('');
  const [totals, setTotals] = useState({ total_cost_usd: 0, total_calls: 0 });
  const [budget, setBudget] = useState(null);
  const [editingBudget, setEditingBudget] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState('');
  const [testResults, setTestResults] = useState({}); // { [agent_key]: {ok, latency_ms, ...} | {loading:true} }
  const importFileRef = useRef(null);

  const refresh = async () => {
    try {
      const [cat, cost, bud] = await Promise.all([
        api.getAgentCatalog(),
        api.getAgentCostSummary(7).catch(() => ({ total_cost_usd: 0, total_calls: 0 })),
        api.getAgentBudget().catch(() => null),
      ]);
      setCatalog(cat);
      setTotals(cost);
      setBudget(bud);
    } catch (err) {
      toast.error(err.message || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleChange = async (key, payload) => {
    setSaving(key);
    try {
      await api.updateAgent(key, payload);
      toast.success('Agent updated');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const handleReset = async (key) => {
    setSaving(key);
    try {
      await api.resetAgent(key);
      toast.success('Reset to recommended');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Reset failed');
    } finally {
      setSaving(null);
    }
  };

  const handleTest = async (key) => {
    setTestResults((prev) => ({ ...prev, [key]: { loading: true } }));
    try {
      const res = await api.testAgent(key);
      setTestResults((prev) => ({ ...prev, [key]: res }));
      if (!res.ok) toast.error(`Test failed for ${key}`);
    } catch (err) {
      setTestResults((prev) => ({ ...prev, [key]: { ok: false, error: err.message } }));
    }
  };

  const handleBudgetSave = async () => {
    const v = parseFloat(budgetDraft);
    if (!Number.isFinite(v) || v < 0) { toast.error('Enter a non-negative dollar amount'); return; }
    try {
      await api.setAgentBudget({ daily_budget_usd: v });
      setEditingBudget(false);
      toast.success('Daily budget updated');
      await refresh();
    } catch (err) { toast.error(err.message || 'Failed to save budget'); }
  };

  const handleCapChange = async (patch) => {
    // Send the current daily budget plus the patched fields (endpoint
    // requires daily_budget_usd on every PUT).
    if (!budget) return;
    try {
      await api.setAgentBudget({ daily_budget_usd: budget.daily_budget_usd, ...patch });
      toast.success('Saved');
      await refresh();
    } catch (err) { toast.error(err.message || 'Failed to save'); }
  };

  const handleExport = async () => {
    try {
      const data = await api.exportAgents();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inzara-agents-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Agent preset exported');
    } catch (err) { toast.error(err.message || 'Export failed'); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const res = await api.importAgents(parsed);
      toast.success(`Imported ${res.applied} agent config${res.applied === 1 ? '' : 's'}${res.skipped?.length ? ` · ${res.skipped.length} skipped` : ''}`);
      await refresh();
    } catch (err) { toast.error(err.message || 'Import failed'); }
  };

  const grouped = useMemo(() => {
    if (!catalog?.agents) return {};
    const q = query.trim().toLowerCase();
    const g = { investigation: [], sentinel: [], insight: [] };
    catalog.agents.forEach((a) => {
      if (q && !a.label.toLowerCase().includes(q) && !a.description.toLowerCase().includes(q) && !a.key.includes(q)) return;
      (g[a.category] || (g[a.category] = [])).push(a);
    });
    return g;
  }, [catalog, query]);

  if (loading) {
    return (
      <div className="p-6" data-testid="ai-agents-loading">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="ai-agents-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
            <Zap className="w-6 h-6 text-primary" />
            AI Agents
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Configure which LLM each agent should use across Investigation Why, Sentinels, and Insight audits.
            Every agent falls back to a sensible default, so you only need to change what you care about.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap" data-testid="ai-agents-cost-header">
          <div className="rounded-xl border border-border/50 bg-card/60 px-4 py-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">7d spend</p>
            <p className="text-lg font-semibold text-foreground font-mono">{formatCost(totals.total_cost_usd)}</p>
          </div>
          <div className="rounded-xl border border-border/50 bg-card/60 px-4 py-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">7d calls</p>
            <p className="text-lg font-semibold text-foreground font-mono">{(totals.total_calls || 0).toLocaleString()}</p>
          </div>
          {budget && (
            <div className="rounded-xl border border-border/50 bg-card/60 px-4 py-2 min-w-[180px]" data-testid="ai-agents-budget-card">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Daily budget</p>
                <button
                  type="button"
                  onClick={() => { setEditingBudget(true); setBudgetDraft(String(budget.daily_budget_usd)); }}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="ai-agents-budget-edit"
                  aria-label="Edit daily budget"
                >
                  <Pencil className="w-3 h-3" />
                </button>
              </div>
              {editingBudget ? (
                <div className="flex items-center gap-1 mt-1">
                  <Input
                    value={budgetDraft}
                    onChange={(e) => setBudgetDraft(e.target.value)}
                    className="h-7 w-20 text-xs"
                    data-testid="ai-agents-budget-input"
                  />
                  <Button size="sm" className="h-7 px-2 text-[10px]" onClick={handleBudgetSave} data-testid="ai-agents-budget-save">Save</Button>
                </div>
              ) : (
                <>
                  <p className="text-lg font-semibold text-foreground font-mono">
                    <span className={budget.remaining_usd <= 0 ? 'text-destructive' : ''}>{formatCost(budget.spent_today_usd)}</span>
                    <span className="text-muted-foreground text-xs"> / {formatCost(budget.daily_budget_usd)}</span>
                  </p>
                  <div className="h-1 mt-1 rounded-full bg-muted/50 overflow-hidden">
                    <div
                      className={`h-full ${budget.percent_used >= 90 ? 'bg-destructive' : budget.percent_used >= 70 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                      style={{ width: `${Math.min(100, budget.percent_used || 0)}%` }}
                      data-testid="ai-agents-budget-bar"
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <div className="flex items-center gap-1.5" data-testid="ai-agents-io-buttons">
            <Link to="/admin/investigations-analytics">
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="ai-agents-analytics-link">
                <Activity className="w-3 h-3" /> Analytics
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={handleExport} className="h-8 text-xs gap-1" data-testid="ai-agents-export">
              <Download className="w-3 h-3" /> Export
            </Button>
            <Button variant="outline" size="sm" onClick={() => importFileRef.current?.click()} className="h-8 text-xs gap-1" data-testid="ai-agents-import">
              <Upload className="w-3 h-3" /> Import
            </Button>
            <input ref={importFileRef} type="file" accept="application/json" className="hidden" onChange={handleImportFile} data-testid="ai-agents-import-file" />
          </div>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          placeholder="Search agents…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9 h-9 rounded-lg max-w-md"
          data-testid="ai-agents-search"
        />
      </div>

      {/* Runtime guards: per-tenant concurrency + per-run cost cap + auto-investigate toggle */}
      {budget && (
        <div className="rounded-xl border border-border/50 bg-card/60 p-4 grid gap-3 md:grid-cols-3 items-end" data-testid="ai-agents-guards">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Max concurrent investigations</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min={0} max={100} step={1}
                defaultValue={budget.max_concurrent_investigations ?? 5}
                onBlur={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (Number.isFinite(v) && v !== budget.max_concurrent_investigations) handleCapChange({ max_concurrent_investigations: v });
                }}
                className="h-8 w-24 text-xs"
                data-testid="cap-concurrency-input"
              />
              <span className="text-[10px] text-muted-foreground">0 = unlimited</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Max cost per run (USD)</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min={0} max={100} step={0.1}
                defaultValue={budget.max_cost_per_run_usd ?? 2}
                onBlur={(e) => {
                  const v = parseFloat(e.target.value);
                  if (Number.isFinite(v) && v !== budget.max_cost_per_run_usd) handleCapChange({ max_cost_per_run_usd: v });
                }}
                className="h-8 w-24 text-xs"
                data-testid="cap-per-run-input"
              />
              <span className="text-[10px] text-muted-foreground">0 = unlimited</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Auto-investigate on alert</label>
            <div className="flex items-center gap-2 mt-1" data-testid="auto-investigate-row">
              <input
                type="checkbox"
                checked={!!budget.auto_investigate_on_alert}
                onChange={(e) => handleCapChange({ auto_investigate_on_alert: e.target.checked })}
                className="w-4 h-4"
                data-testid="auto-investigate-checkbox"
              />
              <span className="text-[11px] text-muted-foreground">Fire an Investigate Why when a KPI monitor breaches</span>
            </div>
          </div>
        </div>
      )}

      {Object.entries(grouped).map(([category, agents]) => {
        if (!agents || agents.length === 0) return null;
        const meta = CATEGORY_META[category] || { label: category, icon: Sparkles, tint: 'text-muted-foreground', border: 'border-border/40' };
        const Icon = meta.icon;
        return (
          <section key={category} className={`rounded-2xl border ${meta.border} bg-card/30 p-4`} data-testid={`ai-agents-section-${category}`}>
            <header className="flex items-center gap-2 mb-3">
              <Icon className={`w-4 h-4 ${meta.tint}`} />
              <h2 className="text-sm font-semibold text-foreground">{meta.label}</h2>
              <span className="text-[10px] text-muted-foreground">{agents.length} agent{agents.length === 1 ? '' : 's'}</span>
            </header>
            <div className="space-y-2">
              {agents.map((a) => (
                <AgentRow
                  key={a.key}
                  agent={a}
                  providerModels={catalog?.provider_models}
                  onChange={handleChange}
                  onReset={handleReset}
                  onTest={handleTest}
                  saving={saving}
                  testResult={testResults[a.key]}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
